import { ArrowRight, Building2, UserRound } from "lucide-react";
import { useState, type FormEvent } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Button, Input } from "../../design-system";
import { useAuth } from "../../contexts/AuthContext";
import { errorMessage } from "../../lib/api";
import type { Role } from "../../types/api";

export function LoginPage({ role }: { role: Role }) {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const routeState = location.state as {
    from?: { pathname?: string };
    registered?: boolean;
    verified?: boolean;
    email?: string;
  } | null;
  const [email, setEmail] = useState(routeState?.email ?? "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const isAdmin = role === "ADMIN";
  const successMessage = routeState?.registered
    ? "Conta criada com sucesso. Você já pode entrar."
    : routeState?.verified
      ? "E-mail verificado com sucesso. Você já pode entrar."
      : "";

  if (auth.ready && auth.user)
    return (
      <Navigate
        to={auth.user.role === "ADMIN" ? "/admin" : "/parceiro"}
        replace
      />
    );

  async function submit(event: FormEvent) {
    event.preventDefault();
    setLoading(true);
    setError("");
    try {
      await auth.login(role, email, password);
      const requested = routeState?.from?.pathname;
      navigate(requested ?? (isAdmin ? "/admin" : "/parceiro"), {
        replace: true,
      });
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="auth-form-card">
      <span
        className={`auth-role-icon ${isAdmin ? "auth-role-icon--admin" : ""}`}
      >
        {isAdmin ? <Building2 /> : <UserRound />}
      </span>
      <span className="eyebrow">
        {isAdmin ? "Acesso da clínica" : "Portal do indicador"}
      </span>
      <h2>Que bom ter você de volta</h2>
      <p>Entre com seu e-mail e senha para continuar.</p>
      {successMessage && (
        <div className="alert alert--success" role="status">
          {successMessage}
        </div>
      )}
      {error && (
        <div className="alert alert--error" role="alert">
          {error}
        </div>
      )}
      <form onSubmit={submit} className="form-stack">
        <Input
          label="E-mail"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          placeholder="voce@exemplo.com"
        />
        <Input
          label="Senha"
          type="password"
          autoComplete="current-password"
          required
          minLength={10}
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          placeholder="Sua senha"
        />
        <Button type="submit" loading={loading}>
          Entrar <ArrowRight size={18} />
        </Button>
      </form>
      {!isAdmin && (
        <p className="auth-switch">
          Ainda não participa?{" "}
          <Link to="/parceiro/cadastrar">Crie sua conta</Link>
        </p>
      )}
      <p className="auth-alt">
        <Link to={isAdmin ? "/parceiro/entrar" : "/admin/entrar"}>
          {isAdmin ? "Sou indicador" : "Acesso administrativo"}
        </Link>
      </p>
    </div>
  );
}
